import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { buildSeo } from "@/lib/seo";
import { getSitemapHealth, runSeoConsistencyAudit } from "@/lib/seo-audit.functions";
import { listSeoAlerts } from "@/lib/seo-alerts.functions";

export const Route = createFileRoute("/seo-status")({
  head: () =>
    buildSeo({
      title: "SEO Status Dashboard — USA Lottery Club",
      description: "Sitemap submission dates, Search Console error rates and crawl health.",
      path: "/seo-status",
      noindex: true,
    }),
  component: SeoStatusPage,
});

const fmt = (v: string | null) =>
  v ? new Date(v).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

function SeoStatusPage() {
  const healthFn = useServerFn(getSitemapHealth);
  const auditFn = useServerFn(runSeoConsistencyAudit);
  const alertsFn = useServerFn(listSeoAlerts);

  const health = useQuery({ queryKey: ["seo-sitemap-health"], queryFn: () => healthFn({}) });
  const audit = useQuery({
    queryKey: ["seo-consistency-audit"],
    queryFn: () => auditFn({ data: { sample: 20 } }),
    enabled: false,
  });
  const alerts = useQuery({ queryKey: ["seo-alerts"], queryFn: () => alertsFn({}) });
  const alertRows = alerts.data?.alerts ?? [];
  const openAlerts = alertRows.filter((a) => !a.resolved_at);

  const rows = health.data?.rows ?? [];
  const totalErrors = rows.reduce((n, r) => n + r.errors, 0);
  const totalUrls = rows.reduce((n, r) => n + r.urlCount, 0);
  const errorRate = totalUrls > 0 ? ((totalErrors / totalUrls) * 100).toFixed(2) : "0.00";

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 space-y-6">
        <header>
          <h1 className="text-3xl font-bold">SEO Status Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Sitemap submissions, Search Console error rates and the last successful crawl per sitemap.
          </p>
        </header>

        <div className="grid gap-4 sm:grid-cols-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Sitemaps tracked</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold">{rows.length}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">URLs submitted</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold">{totalUrls.toLocaleString()}</CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Error rate</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold">{errorRate}%</CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>
              Alerts{" "}
              {openAlerts.length > 0 && (
                <span className="ml-2 rounded-full bg-destructive px-2 py-0.5 text-xs text-destructive-foreground align-middle">
                  {openAlerts.length}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {alerts.isLoading && <p className="text-muted-foreground">Loading…</p>}
            {!alerts.isLoading && alertRows.length === 0 && (
              <p className="text-muted-foreground">
                No alerts recorded. The daily check raises one when the Search Console error rate,
                sitemap errors, or crawl staleness pass their thresholds.
              </p>
            )}
            <ul className="space-y-3">
              {alertRows.map((a) => (
                <li key={a.id} className="border-l-4 pl-3" style={{ borderColor: a.severity === "critical" ? "hsl(var(--destructive))" : "hsl(var(--primary))" }}>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="uppercase font-semibold">{a.severity}</span>
                    <span>{a.kind}</span>
                    <span>{fmt(a.created_at)}</span>
                    {a.resolved_at && <span className="text-green-600">resolved</span>}
                  </div>
                  <p className="text-sm">{a.message}</p>
                  <p className="text-xs text-muted-foreground">
                    {a.metric}: {a.value} (threshold {a.threshold})
                  </p>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Search Console sitemaps</CardTitle></CardHeader>
          <CardContent>
            {health.isLoading && <p className="text-muted-foreground">Loading…</p>}
            {health.data?.error && <p className="text-destructive">{health.data.error}</p>}
            {rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-4">Sitemap</th>
                      <th className="py-2 pr-4">Last submitted</th>
                      <th className="py-2 pr-4">Last successful crawl</th>
                      <th className="py-2 pr-4">URLs</th>
                      <th className="py-2 pr-4">Errors</th>
                      <th className="py-2 pr-4">Warnings</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.sitemap} className="border-b last:border-0">
                        <td className="py-2 pr-4 break-all">{r.sitemap.replace("https://usalotteryclub.com", "")}</td>
                        <td className="py-2 pr-4">{fmt(r.lastSubmitted)}</td>
                        <td className="py-2 pr-4">{fmt(r.lastDownloaded)}</td>
                        <td className="py-2 pr-4">{r.urlCount.toLocaleString()}</td>
                        <td className={`py-2 pr-4 ${r.errors > 0 ? "text-destructive font-semibold" : ""}`}>{r.errors}</td>
                        <td className="py-2 pr-4">{r.warnings}</td>
                        <td className="py-2">{r.isPending ? "Pending" : "Processed"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex-row items-center justify-between gap-4">
            <CardTitle>Runtime indexing consistency</CardTitle>
            <Button size="sm" onClick={() => audit.refetch()} disabled={audit.isFetching} aria-label="Run indexing consistency check">
              {audit.isFetching ? "Checking…" : "Run check"}
            </Button>
          </CardHeader>
          <CardContent>
            {!audit.data && !audit.isFetching && (
              <p className="text-muted-foreground">
                Checks meta robots, X-Robots-Tag, canonical and hreflang on a live sample of routes against robots.txt
                and sitemap membership.
              </p>
            )}
            {audit.data && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {audit.data.urlCount.toLocaleString()} sitemap URLs across {audit.data.sitemapCount} sitemaps ·{" "}
                  {audit.data.sampled} pages sampled · checked {fmt(audit.data.checkedAt)}
                </p>
                {audit.data.ok ? (
                  <p className="text-emerald-600 font-medium">No inconsistencies found.</p>
                ) : (
                  <ul className="space-y-2 text-sm">
                    {audit.data.issues.map((i, idx) => (
                      <li key={`${i.path}-${idx}`} className="border-l-2 border-destructive pl-3">
                        <span className="font-medium">{i.path}</span> — <span className="text-muted-foreground">{i.rule}</span>: {i.detail}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
